import { Stack } from "@mui/material";
import type {
  GridAutoGeneratedGroupNode,
  GridDataGroupNode,
  GridLeafNode,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import type { JSX } from "react";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";

interface Props extends GridRenderCellParams {
  showCount?: boolean;
  showGroupColumnTextInTooltip?: boolean;
  renderGroupCellElement?: (
    value: unknown,
    node: GridDataGroupNode | GridAutoGeneratedGroupNode | GridLeafNode
  ) => JSX.Element;
  renderCellElement?: (row: unknown) => JSX.Element;
  onRowExpand?: () => void;
}

const DataGridGroupingCell = ({
  id,
  value,
  rowNode,
  showCount,
  showGroupColumnTextInTooltip,
  renderGroupCellElement,
  renderCellElement,
  onRowExpand,
  row,
}: Props) => {
  const apiRef = useGridApiContext();

  const isGroupingField = rowNode.depth === 0 && rowNode.type === "group";
  const isGroupingLeaf = rowNode.depth === 0 && rowNode.type === "leaf";

  const handleToggle = () => {
    if (!isGroupingField) {
      return;
    }

    const newExpanded = !rowNode.childrenExpanded;
    apiRef.current.setRowChildrenExpansion(id, newExpanded);
    if (newExpanded && onRowExpand) {
      onRowExpand();
    }
  };

  if (isGroupingField || isGroupingLeaf) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          maxWidth: "100%",
          position: showGroupColumnTextInTooltip ? "inherit" : "absolute",
          pr: 2,
        }}
      >
        {isGroupingField && (
          <ExpandCollapseButton
            expanded={rowNode.childrenExpanded === true}
            onToggle={handleToggle}
            tabIndex={-1}
            ExpandIcon={ExpandIcon}
            CollapseIcon={CollapseIcon}
          />
        )}
        {renderGroupCellElement && renderGroupCellElement(value, rowNode)}
        {!renderGroupCellElement && (
          <TypographyTooltipEllipsis
            text={stringifyValue(value)}
            typographyProps={{ variant: "subtitle1", sx: { mr: 1, fontWeight: [500] } }}
          />
        )}
        {isGroupingField && showCount && rowNode.children && (
          <BadgeDetached color="secondary" showZero badgeContent={rowNode.children.length} />
        )}
      </Stack>
    );
  }

  if (renderCellElement) {
    return renderCellElement(row);
  }

  return <TypographyTooltipEllipsis text={stringifyValue(value)} typographyProps={{ sx: { ml: 8 } }} />;
};

export default DataGridGroupingCell;
